import { kebabCase } from 'lodash';
import React from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import PopoverRB from 'react-bootstrap/Popover';
import { i18n } from '../../i18n/translate';
import { formatClassName, pickDataAttributes } from '../../utils';
import { Slugify } from '../../utils/StringUtil';
import { Button } from '../Button';
import { FAIcon } from '../fa-icon';
import { copyToClipboard } from './utils';
import { IconCategoryValueProps, LinkValue } from './types';

export const IconCategoryValue = <C,>(props: IconCategoryValueProps<C>) => {
  const {
    category,
    className,
    color = 'default',
    copyValue,
    dataAttributesForEdit = {},
    editAction,
    icon,
    iconProps,
    linkValues,
    tooltip,
    tooltipPlacement = 'right',
    type = 'inline',
    url,
    value,
  } = props;

  const dataAtrName = value ? `${kebabCase(String(value))}-icon-category-value` : 'icon-category-value';

  const dataAttributes = pickDataAttributes(props, dataAtrName);

  const renderLinkValues = () => {
    if (!linkValues) return;
    const getNewWindowProps = (link: LinkValue): object => {
      if (link?.newWindow) return { target: '_blank', rel: 'noopener noreferrer' };
      return {};
    };

    if (Array.isArray(linkValues)) {
      return linkValues.map((link, index) => {
        const newWindowProps = getNewWindowProps(link);
        const isLastLink = index === linkValues.length - 1;
        return (
          <React.Fragment key={`link-${index}`}>
            <a href={link.url} {...newWindowProps}>
              {link.label ? link.label : link.url} {link.newWindow && <FAIcon icon="arrow-up-right-from-square" />}
            </a>
            {!isLastLink && ', '}
          </React.Fragment>
        );
      });
    }
    const newWindowProps = getNewWindowProps(linkValues);
    return (
      <a href={linkValues.url} {...newWindowProps}>
        {linkValues.label ? linkValues.label : linkValues.url}
      </a>
    );
  };

  return (
    <div
      {...dataAttributes}
      className={formatClassName([
        'icon-category-value',
        `${type}-icon-category-value`,
        `${color}-icon-category-value`,
        className,
      ])}
    >
      {/* TODO: remove this ignore after FAIcon is refactored to typescript */}
      {/* @ts-ignore */}
      {icon && <FAIcon {...iconProps} additionalClasses="icon" icon={icon} />}

      <span className="category-value">
        {category && <span className={formatClassName(['category', 'bold', `${type}-category`])}>{category}</span>}

        {type === 'stacked' && (
          // @ts-ignore
          <ButtonElements
            copyValue={copyValue}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            editAction={editAction}
            dataAttributesForEdit={dataAttributesForEdit}
            category={category}
            value={value}
          />
        )}

        <span className={formatClassName(['value', `${type}-value`])}>
          {url ? <a href={url}>{value || url}</a> : Array.isArray(value) ? value.join(', ') : value}
          {linkValues && renderLinkValues()}

          {type === 'inline' && (
            // @ts-ignore
            <ButtonElements
              copyValue={copyValue}
              tooltip={tooltip}
              tooltipPlacement={tooltipPlacement}
              editAction={editAction}
              dataAttributesForEdit={dataAttributesForEdit}
              category={category}
              value={value}
            />
          )}
        </span>
      </span>
    </div>
  );
};

interface ButtonElementsProps<C>
  extends Pick<
    IconCategoryValueProps<C>,
    'value' | 'copyValue' | 'tooltip' | 'tooltipPlacement' | 'editAction' | 'category' | 'dataAttributesForEdit'
  > {}

export const ButtonElements = <C,>(props: ButtonElementsProps<C>) => {
  const { copyValue, tooltip, tooltipPlacement, editAction, dataAttributesForEdit, category, value } = props;

  const overlay = (
    <PopoverRB id={`tooltip-${Slugify(category || value)}`}>
      <PopoverRB.Body>{tooltip}</PopoverRB.Body>
    </PopoverRB>
  );

  return (
    <>
      {copyValue && (
        <Button className="m-l-5" icon="copy" onClick={() => copyToClipboard(copyValue)} style="link" renderAsLegacy />
      )}

      {tooltip && (
        <OverlayTrigger placement={tooltipPlacement} transition={false} trigger={['hover', 'focus']} overlay={overlay}>
          <span>
            <Button className="info" style="link" icon="info-circle" renderAsLegacy />
          </span>
        </OverlayTrigger>
      )}

      {editAction && (
        <Button
          {...dataAttributesForEdit}
          className="edit"
          style="link"
          icon="pen"
          label={i18n.t('Edit')}
          onClick={editAction}
        />
      )}
    </>
  );
};
